import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "../App";
import LiveStatsWidget from "../components/LiveStatsWidget";
import TwoFAChallenge from "../components/TwoFAChallenge";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/203f7def-ad2c-4f8f-807f-2bc10f2f16d6/images/a913d16ba95302b639f95999ec0d2a7b5d654d2a1647e0be42e9b257d09c7d8a.png";

const LoginPage = ({ setToken, setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });
  // Fase D — 2FA challenge state
  const [challenge, setChallenge] = useState(null); // {challenge_id, masked_email}

  const completeLogin = (data) => {
    localStorage.setItem("token", data.token);
    try { localStorage.setItem("user", JSON.stringify(data.user)); } catch { /* quota */ }
    setToken(data.token);
    setUser(data.user);
    toast.success("Login realizado com sucesso!");
    const redirect = searchParams.get("redirect");
    navigate(redirect && redirect.startsWith("/") ? redirect : "/dashboard");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let deviceToken = null;
      try { deviceToken = localStorage.getItem("twofa_device_token"); } catch { /* ignore */ }
      const url = deviceToken
        ? `${API}/auth/login?device_token=${encodeURIComponent(deviceToken)}`
        : `${API}/auth/login`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.twofa_required) {
          setChallenge({
            challenge_id: data.challenge_id,
            masked_email: data.masked_email,
          });
        } else {
          completeLogin(data);
        }
      } else {
        toast.error(data.detail || "Email ou senha inválidos");
      }
    } catch (error) {
      toast.error("Erro ao conectar com o servidor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        {challenge ? (
          <TwoFAChallenge
            challengeId={challenge.challenge_id}
            maskedEmail={challenge.masked_email}
            onVerified={completeLogin}
            onCancel={() => setChallenge(null)}
          />
        ) : (
        <div className="bg-white rounded-3xl shadow-2xl p-10">
          <div className="text-center mb-10">
            <Link to="/" className="inline-block">
              <img loading="lazy" decoding="async" src={LOGO_URL} alt="GoFood" className="w-24 h-24 mx-auto mb-5" />
            </Link>
            <h1 className="font-heading text-4xl font-bold mb-2" data-testid="login-title">Entrar no GoFood</h1>
            <p className="text-gray-600 font-body text-lg">Bem-vindo de volta!</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-base font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                className="mt-2 h-12 text-base"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-base font-semibold">Senha</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-semibold text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Esqueceu a senha?
                </Link>
              </div>
              <PasswordInput
                id="password"
                required
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="h-12 text-base"
                containerClassName="mt-2"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-primary to-orange-500 hover:from-primary/90 hover:to-orange-500/90 text-white font-bold text-lg py-7 rounded-full"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-gray-600 font-body text-base">
              Não tem uma conta?{" "}
              <Link to="/register" className="text-primary font-bold hover:underline">
                Cadastre-se
              </Link>
            </p>
          </div>
        </div>
        )}

        {/* Contadores ao vivo */}
        <div className="mt-5 flex justify-center" data-testid="login-live-stats">
          <div className="rounded-full bg-zinc-900/90 backdrop-blur-md border border-white/10 shadow-xl px-4 py-2">
            <LiveStatsWidget variant="inline" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
